import { useMemo, useState } from "react";
import { BuiltGameState, SaberGameSnapshot, SaberStatLine } from "src/utils/sabertooth-client";
import { PlayerMap, playerFullName, stat } from "./game-stream-helpers";
import Styles from "./BookCompare.module.scss";

interface Props {
  // Our scorebook (from the Box Score tab's game state).
  ourBuilt: BuiltGameState;
  ourState: SaberGameSnapshot;
  ourPlayers: PlayerMap;
  // The opponent's scorebook (their progenitor team's stream).
  theirBuilt: BuiltGameState;
  theirState: SaberGameSnapshot;
  theirPlayers: PlayerMap;
  ourTeamId: string; // our team id as it appears in our book (= team.id)
  opponentTeamId: string; // the opponent's team id as it appears in their book (= progenitor)
  ourName: string;
  opponentName: string;
}

type LineEntry = { totalRuns: number; totalHits: number; totalErrors: number } | undefined;

// Each column reads a team-level total from whichever source holds it: R/H/E come from the
// linescore (engine-computed, reliable), the rest from the team's aggregated offense line.
const TEAM_COLUMNS: { key: string; from: (line: LineEntry, offense: SaberStatLine | undefined) => number }[] = [
  { key: "R", from: (l) => l?.totalRuns ?? 0 },
  { key: "H", from: (l) => l?.totalHits ?? 0 },
  { key: "E", from: (l) => l?.totalErrors ?? 0 },
  { key: "2B", from: (_l, o) => stat(o, "2B") },
  { key: "HR", from: (_l, o) => stat(o, "HR") },
  { key: "BB", from: (_l, o) => stat(o, "BB") },
  { key: "SO", from: (_l, o) => stat(o, "SO") },
];

const BATTING_COLUMNS = ["AB", "R", "H", "RBI", "BB", "SO"] as const;

// The same physical team has a different id in each book (each book keeps its own roster plus a
// "shadow" of the other team), so the two sides are paired by which team they represent, not by id.
function teamRow(state: SaberGameSnapshot, teamId: string) {
  return {
    line: state.linescoreWithStatTotals[teamId] as LineEntry,
    offense: state.teamStats[teamId]?.stats?.offense,
  };
}

interface BookPlayer {
  num: string | null;
  name: string;
  offense?: SaberStatLine;
}

interface PlayerDiff {
  num: string;
  name: string;
  ours?: SaberStatLine;
  theirs?: SaberStatLine;
  kind: "diff" | "only-ours" | "only-theirs";
}

// One book's batters for a team. The two books share no player ids, so we carry the jersey number
// and name (both come resolved on the stream) for cross-book matching below.
function collectBatters(state: SaberGameSnapshot, teamId: string, pmap: PlayerMap): BookPlayer[] {
  const out: BookPlayer[] = [];
  for (const [pid, p] of Object.entries(state.teamStats[teamId]?.players ?? {})) {
    const raw = `${pmap[pid]?.number ?? ""}`.trim();
    out.push({ num: raw && raw !== "NaN" ? raw : null, name: playerFullName(pmap[pid]), offense: p.stats?.offense });
  }
  return out;
}

// Loose name key for the fallback match: first name + last initial, so "Nash Herring" (our book)
// and "Nash H" (their abbreviated record) collapse to the same key.
function nameKey(name: string): string | null {
  const parts = name.toLowerCase().replace(/[^a-z ]/g, " ").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  const lastInitial = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${parts[0]}|${lastInitial}`;
}

// Pairs leftover players from each book by a key, but ONLY when that key is unambiguous on both
// sides (appears exactly once) — so a shared/blank key or a duplicate never forces a wrong pairing.
// Returns the still-unmatched remainder of each side.
function pairBy(
  keyOf: (p: BookPlayer) => string | null,
  ourLeft: BookPlayer[],
  theirLeft: BookPlayer[],
  pairs: { our: BookPlayer; their: BookPlayer }[]
): [BookPlayer[], BookPlayer[]] {
  const tally = (arr: BookPlayer[]) => {
    const m: Record<string, number> = {};
    arr.forEach((p) => {
      const k = keyOf(p);
      if (k) m[k] = (m[k] ?? 0) + 1;
    });
    return m;
  };
  const ourCount = tally(ourLeft);
  const theirCount = tally(theirLeft);
  const theirByKey: Record<string, BookPlayer> = {};
  theirLeft.forEach((t) => {
    const k = keyOf(t);
    if (k && theirCount[k] === 1) theirByKey[k] = t;
  });

  const matchedTheir = new Set<BookPlayer>();
  const ourRemain: BookPlayer[] = [];
  ourLeft.forEach((o) => {
    const k = keyOf(o);
    const t = k && ourCount[k] === 1 ? theirByKey[k] : undefined;
    if (t && !matchedTheir.has(t)) {
      pairs.push({ our: o, their: t });
      matchedTheir.add(t);
    } else {
      ourRemain.push(o);
    }
  });
  return [ourRemain, theirLeft.filter((t) => !matchedTheir.has(t))];
}

// Per-player batting-line differences for one team. Players are matched across the two books first
// by jersey number, then (for whatever's left) by name — which recovers a player whose number was
// mistyped or omitted in one book. Anything still unmatched is reported as such rather than dropped
// or double-listed, so a number that doesn't line up is surfaced honestly, not silently mishandled.
function comparePlayers(
  ourState: SaberGameSnapshot,
  ourTeamIdInBook: string,
  ourPlayers: PlayerMap,
  theirState: SaberGameSnapshot,
  theirTeamIdInBook: string,
  theirPlayers: PlayerMap
): { diffs: PlayerDiff[]; identical: number } {
  const ours = collectBatters(ourState, ourTeamIdInBook, ourPlayers);
  const theirs = collectBatters(theirState, theirTeamIdInBook, theirPlayers);

  const pairs: { our: BookPlayer; their: BookPlayer }[] = [];
  let [ourLeft, theirLeft] = pairBy((p) => p.num, ours, theirs, pairs);
  [ourLeft, theirLeft] = pairBy((p) => nameKey(p.name), ourLeft, theirLeft, pairs);

  const hasLine = (o?: SaberStatLine) => BATTING_COLUMNS.some((k) => stat(o, k) > 0);
  const diffs: PlayerDiff[] = [];
  let identical = 0;

  for (const { our, their } of pairs) {
    const differs = BATTING_COLUMNS.some((k) => stat(our.offense, k) !== stat(their.offense, k));
    if (differs) {
      diffs.push({ num: our.num ?? their.num ?? "", name: our.name || their.name, ours: our.offense, theirs: their.offense, kind: "diff" });
    } else {
      identical += 1;
    }
  }
  // Truly unmatched players (couldn't pair by number or name). Only surface ones who actually did
  // something at the plate, so a defensive sub recorded in one book isn't flagged as a discrepancy.
  for (const o of ourLeft) if (hasLine(o.offense)) diffs.push({ num: o.num ?? "", name: o.name, ours: o.offense, kind: "only-ours" });
  for (const t of theirLeft) if (hasLine(t.offense)) diffs.push({ num: t.num ?? "", name: t.name, theirs: t.offense, kind: "only-theirs" });

  diffs.sort((a, b) => (parseInt(a.num, 10) || 999) - (parseInt(b.num, 10) || 999));
  return { diffs, identical };
}

export default function BookCompare({
  ourBuilt,
  ourState,
  ourPlayers,
  theirBuilt,
  theirState,
  theirPlayers,
  ourTeamId,
  opponentTeamId,
  ourName,
  opponentName,
}: Props) {
  const [showPlayers, setShowPlayers] = useState(false);

  // Resolve the "other" team id in each book: in our book it's the opponent, in theirs it's us.
  const oppIdInOurBook = [ourBuilt.awayTeamId, ourBuilt.homeTeamId].find((id) => id !== ourTeamId);
  const ourIdInTheirBook = [theirBuilt.awayTeamId, theirBuilt.homeTeamId].find((id) => id !== opponentTeamId);

  const teams = useMemo(
    () => [
      {
        name: ourName,
        yours: teamRow(ourState, ourTeamId),
        theirs: ourIdInTheirBook ? teamRow(theirState, ourIdInTheirBook) : undefined,
        players:
          ourIdInTheirBook != null
            ? comparePlayers(ourState, ourTeamId, ourPlayers, theirState, ourIdInTheirBook, theirPlayers)
            : { diffs: [], identical: 0 },
      },
      {
        name: opponentName,
        yours: oppIdInOurBook ? teamRow(ourState, oppIdInOurBook) : undefined,
        theirs: teamRow(theirState, opponentTeamId),
        players:
          oppIdInOurBook != null
            ? comparePlayers(ourState, oppIdInOurBook, ourPlayers, theirState, opponentTeamId, theirPlayers)
            : { diffs: [], identical: 0 },
      },
    ],
    [ourState, theirState, ourTeamId, opponentTeamId, oppIdInOurBook, ourIdInTheirBook, ourPlayers, theirPlayers, ourName, opponentName]
  );

  const teamDiffCount = teams.reduce(
    (n, t) => n + TEAM_COLUMNS.filter((c) => c.from(t.yours?.line, t.yours?.offense) !== c.from(t.theirs?.line, t.theirs?.offense)).length,
    0
  );
  const playerDiffCount = teams.reduce((n, t) => n + t.players.diffs.length, 0);

  return (
    <div className={Styles.compare}>
      <div className={teamDiffCount === 0 ? Styles.summaryAgree : Styles.summary}>
        {teamDiffCount === 0
          ? "Both scorebooks agree on the team totals."
          : `The scorebooks disagree on ${teamDiffCount} team total${teamDiffCount === 1 ? "" : "s"} — highlighted below.`}
      </div>

      {teams.map((t) => (
        <section className={Styles.team} key={t.name}>
          <h4 className={Styles.teamName}>{t.name}</h4>
          <div className={Styles.tableWrap}>
            <table className={Styles.table}>
              <thead>
                <tr>
                  <th className={Styles.rowLabel}>Scorebook</th>
                  {TEAM_COLUMNS.map((c) => (
                    <th key={c.key}>{c.key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(["yours", "theirs"] as const).map((side) => {
                  const row = t[side];
                  return (
                    <tr key={side}>
                      <td className={Styles.rowLabel}>{side === "yours" ? "Your book" : "Their book"}</td>
                      {TEAM_COLUMNS.map((c) => {
                        const differs = c.from(t.yours?.line, t.yours?.offense) !== c.from(t.theirs?.line, t.theirs?.offense);
                        return (
                          <td key={c.key} className={differs ? Styles.diff : undefined}>
                            {c.from(row?.line, row?.offense)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {showPlayers && <PlayerDiffs team={t} />}
        </section>
      ))}

      {playerDiffCount > 0 ? (
        <button type="button" className={Styles.toggle} onClick={() => setShowPlayers((v) => !v)}>
          {showPlayers ? "Hide player-level differences" : `Show player-level differences (${playerDiffCount})`}
        </button>
      ) : (
        <div className={Styles.note}>Both books recorded every matched player’s batting line identically.</div>
      )}

      <p className={Styles.note}>
        Each team keeps its own scorebook, so judgment calls (hit vs. error, earned runs) can differ. Players are matched
        across books by jersey number — the books don’t share player records.
      </p>
    </div>
  );
}

// The per-player batting differences for one team, shown under its totals when expanded.
function PlayerDiffs({ team }: { team: { players: { diffs: PlayerDiff[]; identical: number } } }) {
  const { diffs, identical } = team.players;
  if (diffs.length === 0) {
    return <div className={Styles.playerNote}>All {identical} matched players scored identically.</div>;
  }
  return (
    <div className={Styles.players}>
      {diffs.map((d) => (
        <div className={Styles.playerBlock} key={d.num}>
          <div className={Styles.playerName}>
            {d.num ? `#${d.num} ` : ""}
            {d.name}
            {d.kind === "only-ours" && <span className={Styles.onlyTag}> · no match in their book</span>}
            {d.kind === "only-theirs" && <span className={Styles.onlyTag}> · no match in your book</span>}
          </div>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th className={Styles.rowLabel}>Scorebook</th>
                {BATTING_COLUMNS.map((c) => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={Styles.rowLabel}>Your book</td>
                {BATTING_COLUMNS.map((c) => {
                  const differs = stat(d.ours, c) !== stat(d.theirs, c);
                  return (
                    <td key={c} className={d.ours ? (differs ? Styles.diff : undefined) : Styles.absent}>
                      {d.ours ? stat(d.ours, c) : "—"}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className={Styles.rowLabel}>Their book</td>
                {BATTING_COLUMNS.map((c) => {
                  const differs = stat(d.ours, c) !== stat(d.theirs, c);
                  return (
                    <td key={c} className={d.theirs ? (differs ? Styles.diff : undefined) : Styles.absent}>
                      {d.theirs ? stat(d.theirs, c) : "—"}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
